import { useEffect, useState } from "react"
import { listExperiments, createExperiment } from "@/services/api"
import type { Experiment } from "@/types"
import { Panel, Stat, StatGrid, Badge } from "@/components/ui"
import { Plus } from "lucide-react"

const ALGORITHMS = [
  { value: "ppo_extended", label: "PPO Extended", obs: "extended" },
  { value: "ppo_basic", label: "PPO Basic", obs: "basic" },
  { value: "hand_rule", label: "Hand Rule", obs: null },
  { value: "random", label: "Random", obs: null },
] as const

export default function ExperimentsPage() {
  const [experiments, setExperiments] = useState<Experiment[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({
    name: "",
    description: "",
    algorithm: "ppo_extended" as string,
    obs_mode: "extended" as string | null,
    seed: 0 as number | null,
    is_demo: false,
  })

  useEffect(() => {
    load()
  }, [])

  async function load() {
    const res = await listExperiments()
    setExperiments(res.data)
    setLoading(false)
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setCreating(true)
    try {
      const res = await createExperiment({
        name: form.name,
        description: form.description || null,
        algorithm: form.algorithm,
        obs_mode: form.obs_mode,
        seed: form.seed,
        config: { demo: form.is_demo },
        is_demo: form.is_demo,
      })
      setExperiments((prev) => [...prev, res.data])
      setForm({
        name: "",
        description: "",
        algorithm: "ppo_extended",
        obs_mode: "extended",
        seed: 0,
        is_demo: false,
      })
    } catch (err) {
      alert("Failed to create experiment")
    } finally {
      setCreating(false)
    }
  }

  if (loading) return <div className="text-muted text-sm">Loading experiments&hellip;</div>

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Rocket Configuration</h1>
          <p className="text-muted text-sm">
            Create and manage simulation experiments
          </p>
        </div>
        <button
          onClick={() => setCreating(!creating)}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-400 text-black rounded-sm text-sm font-medium shadow-glow transition"
        >
          <Plus size={16} /> New Experiment
        </button>
      </div>

      {creating && (
        <Panel padded={false}>
          <div className="p-4">
            <form onSubmit={handleCreate} className="space-y-4 max-w-md">
              <div>
                <label className="block text-[0.625rem] font-medium text-dim mb-1">Experiment Name</label>
                <input
                  className="w-full bg-surface border border-border rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan-500 focus:outline-none"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. PPO Extended Sweep"
                />
              </div>
              <div>
                <label className="block text-[0.625rem] font-medium text-dim mb-1">Description</label>
                <input
                  className="w-full bg-surface border border-border rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan-500 focus:outline-none"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[0.625rem] font-medium text-dim mb-1">Algorithm</label>
                  <select
                    className="w-full bg-surface border border-border rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan-500 focus:outline-none"
                    value={form.algorithm}
                    onChange={(e) => {
                      const alg = e.target.value as string
                      const obs = ALGORITHMS.find((a) => a.value === alg)?.obs ?? null
                      setForm({ ...form, algorithm: alg, obs_mode: obs })
                    }}
                  >
                    {ALGORITHMS.map((a) => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-[0.625rem] font-medium text-dim">
                    <input
                      type="checkbox"
                      checked={form.is_demo}
                      onChange={(e) => setForm({ ...form, is_demo: e.target.checked })}
                      className="rounded bg-surface border-border text-cyan-600 focus:ring-cyan-500"
                    />
                    Demo data
                  </label>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-[0.625rem] font-medium text-dim mb-1">Seed</label>
                <input
                  type="number"
                  className="w-full bg-surface border border-border rounded-sm px-3 py-2 text-sm text-ink mono focus:border-cyan-500 focus:outline-none"
                  value={form.seed ?? 0}
                  onChange={(e) => setForm({ ...form, seed: Number(e.target.value) || 0 })}
                />
              </div>
              <button
                type="submit"
                disabled={!form.name || creating}
                className="w-full py-2 bg-cyan-500 hover:bg-cyan-400 text-black rounded-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-glow transition"
              >
                {creating ? "Creating&hellip;" : "Create Experiment"}
              </button>
            </form>
          </div>
        </Panel>
      )}

      <Panel title="All Experiments" padded={false}>
        <div className="p-3">
          {experiments.length === 0 ? (
            <p className="text-muted text-sm">No experiments yet. Create one to get started.</p>
          ) : (
            <div className="space-y-2">
              {experiments.map((exp) => (
                <div
                  key={exp.id}
                  className="flex items-center justify-between p-3 rounded-sm border border-border hover:bg-surface/50 transition group cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-ink">{exp.name}</span>
                      <Badge variant="muted">{exp.is_demo ? "DEMO" : ""}</Badge>
                    </div>
                    <div className="text-[0.625rem] text-dim mt-1">
                      {exp.algorithm.toUpperCase()} &middot; {exp.obs_mode ?? "n/a"} obs &middot; seed{" "}
                      {exp.seed ?? "—"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-mono text-dim truncate max-w-[120px]" title={exp.id}>
                      {exp.id.slice(0, 8)}&hellip;
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Panel>

      <StatGrid cols={3}>
        <Stat label="Total experiments" value={experiments.length.toLocaleString()} status="nominal" accent="blue" />
        <Stat label="Demo experiments" value={experiments.filter((e) => e.is_demo).length.toLocaleString()} status="muted" accent="amber" />
        <Stat label="PPO experiments" value={experiments.filter((e) => e.algorithm.startsWith("ppo")).length.toLocaleString()} status="nominal" accent="cyan" />
      </StatGrid>
    </div>
  )
}
